import {
  AnyPgColumn,
  date,
  foreignKey,
  index,
  integer,
  pgTable,
  text,
  uuid,
} from 'drizzle-orm/pg-core';
import { customer } from '@trashlab/api/src/domains/customer/schemas/customer';
import { orders } from '@trashlab/api/src/domains/orders/schemas/orders';
import { assets } from '@trashlab/api/src/domains/asset/schemas/assets';
import { customerSite } from '@trashlab/api/src/domains/customer/schemas/customer-site';
import { material } from '@trashlab/api/src/domains/pricing/schemas/material';
import { product } from '@trashlab/api/src/domains/pricing/schemas/product';
import { sku_v2 } from '@trashlab/api/src/domains/pricing/schemas/sku-v2';
import { sub_hauler } from '@trashlab/api/src/domains/orders/schemas/sub-hauler';
import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import { asset_movement } from '@trashlab/api/src/domains/asset/schemas/asset-movement';
import { business_line_config } from '@trashlab/api/src/domains/business-lines/schemas/business-line';

/**
 * Represents a single line of service at a given location.
 * This is the canonical root for tracking service lifecycle, replacing service_line chains.
 * A service_line_v2 can exist independently of work orders.
 *
 * Status is derived from dates relative to a reference date (typically "today"):
 * - PENDING: startDate is NULL or startDate > referenceDate
 * - ACTIVE: startDate <= referenceDate AND (endDate is NULL OR endDate > referenceDate)
 * - ENDED: endDate is NOT NULL AND endDate <= referenceDate
 */
export const service_line_v2 = pgTable(
  'service_line_v2',
  {
    ...defaultFields.fieldsWithTenantId(),
    /** Reference to the customer */
    customerId: uuid('customerId').notNull(),
    /** ID of the customer site where the service is performed */
    customerSiteId: uuid('customerSiteId').notNull(),
    /** Date when service started (null = pending/not yet started) */
    startDate: date('startDate', { mode: 'date' }),
    /** Date when service ended (null = still active, set = ended) */
    endDate: date('endDate', { mode: 'date' }),
    /** Reference to the order that delivered the container/material (nullable - can start without order) */
    deliveryOrderId: uuid('deliveryOrderId').references(
      (): AnyPgColumn => orders.id,
    ),
    /** Reference to the order that picked up the container/material */
    pickupOrderId: uuid('pickupOrderId').references(
      (): AnyPgColumn => orders.id,
    ),
    /** Container or equipment used for the service */
    assetId: uuid('assetId'),
    /** Type of product/service being provided */
    productId: uuid('productId'),
    /** Type of material being handled */
    materialId: uuid('materialId'),
    /** Reference to the pricing SKU */
    skuId: uuid('skuId'),
    /** Reference to the latest asset movement */
    latestAssetMovementId: uuid('latestAssetMovementId').references(
      (): AnyPgColumn => asset_movement.id,
    ),
    /** Business line this service line belongs to */
    businessLineId: uuid('businessLineId').references(
      () => business_line_config.id,
    ),
    /** Quantity of items in this service line */
    quantity: integer('quantity').default(1).notNull(),
    /** Additional notes about the service */
    notes: text('notes').default('').notNull(),
    /** Reference to third-party hauler if used */
    subHaulerId: uuid('subHaulerId'),
    /** Date of last automated days-based charge */
    lastAutomatedChargeOfDaysDate: date('lastAutomatedChargeOfDaysDate'),
    /** Date of last service performed */
    lastServiceDate: date('lastServiceDate'),
  },
  (table) => {
    return {
      // Indexes
      tenantId_idx: index('service_line_v2_tenantId_idx').on(table.tenantId),
      customerId_idx: index('service_line_v2_customerId_idx').on(
        table.customerId,
      ),
      customerSiteId_idx: index('service_line_v2_customerSiteId_idx').on(
        table.customerSiteId,
      ),
      startDate_idx: index('service_line_v2_startDate_idx').on(table.startDate),
      endDate_idx: index('service_line_v2_endDate_idx').on(table.endDate),
      tenant_site_dates_idx: index('service_line_v2_tenant_site_dates_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.startDate,
        table.endDate,
      ),
      deliveryOrderId_idx: index('service_line_v2_deliveryOrderId_idx').on(
        table.deliveryOrderId,
      ),
      pickupOrderId_idx: index('service_line_v2_pickupOrderId_idx').on(
        table.pickupOrderId,
      ),
      assetId_idx: index('service_line_v2_assetId_idx').on(table.assetId),
      productId_idx: index('service_line_v2_productId_idx').on(table.productId),
      materialId_idx: index('service_line_v2_materialId_idx').on(
        table.materialId,
      ),
      skuId_idx: index('service_line_v2_skuId_idx').on(table.skuId),
      businessLineId_idx: index('service_line_v2_businessLineId_idx').on(
        table.businessLineId,
      ),
      subHaulerId_idx: index('service_line_v2_subHaulerId_idx').on(
        table.subHaulerId,
      ),
      latestAssetMovementId_idx: index(
        'service_line_v2_latestAssetMovementId_idx',
      ).on(table.latestAssetMovementId),

      // Foreign keys
      service_line_v2_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'service_line_v2_customerId_fkey',
      }),
      service_line_v2_customerSiteId_fkey: foreignKey({
        columns: [table.customerSiteId],
        foreignColumns: [customerSite.id],
        name: 'service_line_v2_customerSiteId_fkey',
      }),
      service_line_v2_assetId_fkey: foreignKey({
        columns: [table.assetId],
        foreignColumns: [assets.id],
        name: 'service_line_v2_assetId_fkey',
      }),
      service_line_v2_productId_fkey: foreignKey({
        columns: [table.productId],
        foreignColumns: [product.id],
        name: 'service_line_v2_productId_fkey',
      }),
      service_line_v2_materialId_fkey: foreignKey({
        columns: [table.materialId],
        foreignColumns: [material.id],
        name: 'service_line_v2_materialId_fkey',
      }),
      service_line_v2_skuId_fkey: foreignKey({
        columns: [table.skuId],
        foreignColumns: [sku_v2.id],
        name: 'service_line_v2_skuId_fkey',
      }),
      service_line_v2_subHaulerId_fkey: foreignKey({
        columns: [table.subHaulerId],
        foreignColumns: [sub_hauler.id],
        name: 'service_line_v2_subHaulerId_fkey',
      }),
    };
  },
);

/**
 * Historical record of changes to service_line_v2.
 * Tracks all modifications over time for auditing and versioning.
 * Contains the same fields but allows nulls for partial updates.
 */
export const service_line_v2_history = pgTable(
  'service_line_v2_history',
  {
    ...defaultHistoryFields.historyFieldsWithTenantId(),
    customerId: uuid('customerId'),
    customerSiteId: uuid('customerSiteId'),
    startDate: date('startDate', { mode: 'date' }),
    endDate: date('endDate', { mode: 'date' }),
    deliveryOrderId: uuid('deliveryOrderId'),
    pickupOrderId: uuid('pickupOrderId'),
    assetId: uuid('assetId'),
    productId: uuid('productId'),
    materialId: uuid('materialId'),
    skuId: uuid('skuId'),
    latestAssetMovementId: uuid('latestAssetMovementId'),
    businessLineId: uuid('businessLineId'),
    quantity: integer('quantity'),
    notes: text('notes'),
    subHaulerId: uuid('subHaulerId'),
    lastAutomatedChargeOfDaysDate: date('lastAutomatedChargeOfDaysDate'),
    lastServiceDate: date('lastServiceDate'),
  },
  (table) => {
    return {
      id_version_number_idx: index(
        'service_line_v2_history_id_version_number_idx',
      ).on(table.id, table.versionNumber),
      customerSiteId_idx: index(
        'service_line_v2_history_customerSiteId_idx',
      ).on(table.customerSiteId),
      deliveryOrderId_idx: index(
        'service_line_v2_history_deliveryOrderId_idx',
      ).on(table.deliveryOrderId),
      pickupOrderId_idx: index('service_line_v2_history_pickupOrderId_idx').on(
        table.pickupOrderId,
      ),
    };
  },
);

/** Type inference for the service_line_v2 schema */
export type ServiceLineV2Schema = typeof service_line_v2.$inferSelect;

